import { Crown, Star, Clock, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useState } from 'react';

interface TrialBannerProps {
  trialTier: 'chef_master' | 'pro';
  daysRemaining: number;
  trialEndDate: string;
  className?: string;
}

export const TrialBanner = ({
  trialTier,
  daysRemaining,
  trialEndDate,
  className
}: TrialBannerProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const isChefMaster = trialTier === 'chef_master';
  const isEnding = daysRemaining <= 2;

  const tierName = isChefMaster ? 'Chef Master' : 'Pro';
  const TierIcon = isChefMaster ? Crown : Star;

  return (
    <div
      className={cn(
        "relative flex items-center gap-3 px-4 py-3 rounded-lg border",
        isChefMaster
          ? "bg-amber-500/10 border-amber-500/30"
          : "bg-primary/10 border-primary/30",
        isEnding && "animate-pulse",
        className
      )}
    >
      <div className={cn(
        "flex-shrink-0 p-2 rounded-full",
        isChefMaster ? "bg-amber-500/20" : "bg-primary/20"
      )}>
        <TierIcon className={cn(
          "h-5 w-5",
          isChefMaster ? "text-amber-500" : "text-primary"
        )} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className={cn(
            "text-xs",
            isChefMaster ? "border-amber-500/50 text-amber-600" : "border-primary/50 text-primary"
          )}>
            {t('trial.badge', { tier: tierName })}
          </Badge>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>
              {daysRemaining === 1
                ? t('trial.lastDay')
                : t('trial.daysRemaining', { count: daysRemaining })}
            </span>
          </div>
        </div>
        <p className="text-sm mt-1 text-foreground/80">
          {isEnding
            ? t('trial.endingSoon', { tier: tierName })
            : t('trial.enjoying', { tier: tierName })}
        </p>
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/pricing')}
        className={cn(
          "flex-shrink-0",
          isChefMaster ? "text-amber-600 hover:text-amber-700" : "text-primary"
        )}
      >
        {t('trial.viewPlans')}
        <ChevronRight className="h-4 w-4 ml-1" />
      </Button>

      <Button
        variant="ghost"
        size="icon"
        className="absolute top-1 right-1 h-6 w-6 text-muted-foreground hover:text-foreground"
        onClick={() => setDismissed(true)}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
};

// Compact version for navbar
export const TrialBadge = ({
  trialTier,
  daysRemaining,
  onClick
}: {
  trialTier: 'chef_master' | 'pro';
  daysRemaining: number;
  onClick?: () => void;
}) => {
  const { t } = useTranslation();
  const isChefMaster = trialTier === 'chef_master';
  const tierName = isChefMaster ? 'Chef Master' : 'Pro';
  const TierIcon = isChefMaster ? Crown : Star;

  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 cursor-pointer transition-colors",
        isChefMaster
          ? "border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
          : "border-primary/50 text-primary hover:bg-primary/10"
      )}
      onClick={onClick}
    >
      <TierIcon className="h-3 w-3" />
      <span>{t('trial.badgeCompact', { days: daysRemaining, tier: tierName })}</span>
    </Badge>
  );
};